import { Button, Popover, PopoverContent, PopoverTrigger, Skeleton } from '@kalos/ui';
import { ExternalLinkIcon } from '@radix-ui/react-icons';
import { Link } from '@tanstack/react-router';

import { useEventQuery } from '../hooks/react-query/useEventsQuery';

export const JobPreview = ({ jobId }: { jobId: number }) => {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button size="sm" onClick={(e) => e.stopPropagation()} className="min-w-fit">
          {jobId}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-96 max-w-[90vw]">
        <JobInfo jobId={jobId} />
      </PopoverContent>
    </Popover>
  );
};

export const JobInfo = ({ jobId }: { jobId: number }) => {
  const eventDataQuery = useEventQuery({
    filter: {
      id: jobId,
    },
    select(data) {
      const result = {
        description: '',
        customer: '',
        property: '',
        isProjectJob: false,
      };
      if (data) {
        result.description = data.description;
        result.customer = data.customer
          ? `${data.customer.firstname} ${data.customer.lastname}`
          : '';
        result.property = data.property ? `${data.property.address}, ${data.property.city}` : '';
        result.isProjectJob = data.isProject == true;
      }
      return result;
    },
  });

  if (eventDataQuery.isLoading) {
    return (
      <div className="flex flex-col gap-1">
        <Skeleton className="bg-foreground/30 h-4 w-3/4" />
        <Skeleton className="bg-foreground/30 h-4 w-2/3" />
        <Skeleton className="bg-foreground/30 h-4 w-1/2" />
      </div>
    );
  }
  if (eventDataQuery.isError) return 'Error loading job info';
  if (eventDataQuery.isSuccess) {
    const isProjectJob = eventDataQuery.data.isProjectJob;
    const linkTo = isProjectJob ? '/project/$projectId' : '/jobs/$jobId';
    const linkParams = isProjectJob ? { projectId: jobId } : { jobId: jobId };

    return (
      <div className="flex max-h-[50vh] flex-col gap-3">
        <div className="flex flex-shrink-0 flex-col gap-2 text-sm">
          {eventDataQuery.data.customer && (
            <div>
              <p className="text-muted-foreground mb-1 text-xs font-semibold uppercase">Customer</p>
              <p className="font-medium">{eventDataQuery.data.customer}</p>
            </div>
          )}
          {eventDataQuery.data.property && (
            <div>
              <p className="text-muted-foreground mb-1 text-xs font-semibold uppercase">Property</p>
              <p className="text-sm">{eventDataQuery.data.property}</p>
            </div>
          )}
        </div>
        {eventDataQuery.data.description && (
          <div className="flex min-h-0 flex-col">
            <p className="text-muted-foreground mb-2 flex-shrink-0 text-xs font-semibold uppercase">
              Description
            </p>
            <div className="bg-muted/30 max-h-48 min-h-0 flex-1 overflow-y-auto rounded border p-3">
              <p className="whitespace-pre-wrap break-words text-sm leading-relaxed">
                {eventDataQuery.data.description}
              </p>
            </div>
          </div>
        )}
        <Link to={linkTo} params={linkParams} target="_blank" className="flex-shrink-0">
          <Button size="sm" className="w-full gap-2">
            <ExternalLinkIcon />
            Open {isProjectJob ? 'Project' : 'Job'}
          </Button>
        </Link>
      </div>
    );
  }
};
